import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Play, 
  Calendar, 
  User, 
  Tag, 
  Tv, 
  Target, 
  Lightbulb, 
  Sparkles, 
  Cpu, 
  TrendingUp, 
  Layers, 
  Users, 
  CheckCircle2, 
  ArrowRight,
  ShieldCheck
} from 'lucide-react';
import LazyVideoPlayer from '../components/common/LazyVideoPlayer';
import { projects as projectsTr } from '../data/projects';
import { projectsEn } from '../data/projects.en';
import { useLanguage } from '../context/LanguageContext';
import { tr } from '../locales/tr';
import { en } from '../locales/en';

const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.projectDetail : en.projectDetail;
  const currentProjects = language === 'TR' ? projectsTr : projectsEn;

  // Look up current project
  const project = useMemo(() => {
    return currentProjects.find(p => p.slug === slug);
  }, [slug, currentProjects]);

  // Related projects: 3 matching category (excluding current)
  const relatedProjects = useMemo(() => {
    if (!project) return [];
    return currentProjects
      .filter(p => p.id !== project.id && p.categories.some(cat => project.categories.includes(cat)))
      .slice(0, 3);
  }, [project, currentProjects]);

  // Map video watch URL to iframe embed URL
  const embedUrl = useMemo(() => {
    if (!project || !project.videoUrl) return '';
    const url = project.videoUrl;

    if (project.videoProvider === 'youtube') {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        return `https://www.youtube-nocookie.com/embed/${match[2]}?autoplay=0&rel=0&modestbranding=1`;
      }
    } else if (project.videoProvider === 'vimeo') {
      const regExp = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
      const match = url.match(regExp);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}?title=0&byline=0&portrait=0`;
      }
    }
    return url;
  }, [project]);

  // If project not found, render 404 state
  if (!project) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center text-center p-6">
        <Tv className="w-16 h-16 text-[#ff7a63]/30 mb-6" />
        <h1 className="text-3xl font-display text-white mb-2">{t.notFound}</h1>
        <p className="text-sm text-muted-foreground max-w-sm mb-8">
          {t.notFoundDesc}
        </p>
        <Link
          to={language === 'EN' ? "/en/projects" : "/projeler"}
          className="px-6 py-3 bg-foreground text-background text-xs font-bold uppercase tracking-wider rounded-full hover:bg-foreground/90 transition-all shadow-md"
        >
          {t.backToProjects}
        </Link>
      </div>
    );
  }

  const backProjectsUrl = language === 'EN' ? "/en/projects" : "/projeler";
  const canonicalUrl = language === 'EN' 
    ? `https://www.we24agency.com/en/projects/${project.slug}`
    : `https://www.we24agency.com/projeler/${project.slug}`;
  const alternateTr = `https://www.we24agency.com/projeler/${project.slug}`;
  const alternateEn = `https://www.we24agency.com/en/projects/${project.slug}`;

  return (
    <div className="bg-[#030811] text-foreground min-h-screen">
      <Helmet>
        <title>{`${project.title} - WE24 Agency`}</title>
        <meta name="description" content={project.strategicImpact || project.shortDescription} />
        <meta name="keywords" content={[...project.categories, ...project.services, project.client, "WE24 Agency", "Video Prodüksiyon", "İstanbul", "Creative Agency"].join(', ')} />
        <meta name="author" content="WE24 Agency" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="tr" href={alternateTr} />
        <link rel="alternate" hrefLang="en" href={alternateEn} />
        <link rel="alternate" hrefLang="x-default" href={alternateTr} />
        
        {/* OpenGraph */}
        <meta property="og:title" content={`${project.title} - WE24 Agency`} />
        <meta property="og:description" content={project.strategicImpact || project.shortDescription} />
        <meta property="og:image" content={`https://www.we24agency.com${project.coverImage}`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="video.other" />
        <meta property="og:site_name" content="WE24 Agency" />
        <meta property="og:locale" content={language === 'EN' ? "en_US" : "tr_TR"} />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${project.title} - WE24 Agency`} />
        <meta name="twitter:description" content={project.strategicImpact || project.shortDescription} />
        <meta name="twitter:image" content={`https://www.we24agency.com${project.coverImage}`} />

        {/* Structured Data (JSON-LD) for Search & Generative AI Engines */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": language === 'EN' ? "Home" : "Ana Sayfa",
                    "item": language === 'EN' ? "https://www.we24agency.com/en" : "https://www.we24agency.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": language === 'EN' ? "Projects" : "Projeler",
                    "item": language === 'EN' ? "https://www.we24agency.com/en/projects" : "https://www.we24agency.com/projeler"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": project.title,
                    "item": canonicalUrl
                  }
                ]
              },
              {
                "@type": "CreativeWork",
                "name": project.title,
                "headline": project.title,
                "description": project.strategicImpact || project.shortDescription,
                "image": `https://www.we24agency.com${project.coverImage}`,
                "url": canonicalUrl,
                "inLanguage": language === 'EN' ? "en" : "tr",
                "datePublished": project.year ? `${project.year}-01-01` : "2024-01-01",
                "genre": project.categories.join(', '),
                "keywords": [...project.categories, ...project.services, project.client].join(', '),
                "creator": {
                  "@type": "Organization",
                  "name": "WE24 Agency",
                  "url": "https://www.we24agency.com",
                  "logo": "https://www.we24agency.com/favicon.png"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "WE24 Agency",
                  "url": "https://www.we24agency.com",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://www.we24agency.com/favicon.png"
                  }
                },
                ...(project.videoUrl ? {
                  "video": {
                    "@type": "VideoObject",
                    "name": project.title,
                    "description": project.strategicImpact || project.shortDescription,
                    "thumbnailUrl": `https://www.we24agency.com${project.coverImage}`,
                    "uploadDate": project.year ? `${project.year}-01-01` : "2024-01-01",
                    "contentUrl": project.videoUrl,
                    "embedUrl": embedUrl,
                    "inLanguage": language === 'EN' ? "en" : "tr"
                  }
                } : {})
              }
            ]
          })}
        </script>
      </Helmet>

      {/* 1. Cinematic Project Hero Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 border-b border-white/[0.08] overflow-hidden bg-gradient-to-b from-[#05131f] via-[#030811] to-[#030811]">
        {/* Subtle Ambient Glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-[#ff7a63]/10 blur-[130px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {/* Back Link */}
          <Link
            to={backProjectsUrl}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white mb-8 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            {t.backToProjects}
          </Link>

          {/* Metadata Badges */}
          <div className="flex flex-wrap items-center gap-2.5 mb-6">
            {project.categories.map(cat => {
              const categoryMap: Record<string, string> = {
                "Canlı Yayın": "Live Broadcast",
                "Tanıtım Filmleri": "Promotional Films",
                "Reklam Filmleri": "Commercials",
                "Belgeseller": "Documentaries",
                "Müzik Klipleri": "Music Videos",
                "Web Siteleri": "Web Development",
                "Sosyal Medya": "Social Media"
              };
              const displayCat = language === 'EN' ? (categoryMap[cat] || cat) : cat;
              return (
                <span key={cat} className="text-xs uppercase font-semibold tracking-wider text-[#ff7a63] bg-[#ff7a63]/10 border border-[#ff7a63]/25 px-3.5 py-1 rounded-full backdrop-blur-md">
                  {displayCat}
                </span>
              );
            })}
            {project.year && (
              <span className="text-xs font-mono font-medium text-zinc-400 bg-white/[0.04] border border-white/[0.08] px-3 py-1 rounded-full">
                {project.year}
              </span>
            )}
            <span className="text-xs font-medium text-zinc-300 bg-white/[0.04] border border-white/[0.08] px-3.5 py-1 rounded-full flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-[#ff7a63]" />
              {project.client}
            </span>
          </div>

          {/* Grand Editorial Project Title */}
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-display text-white tracking-tight leading-[1.08] max-w-5xl mb-6">
            {project.title}
          </h1>

          {/* Strategic Impact Highlight */}
          <p className="text-base sm:text-lg md:text-xl text-zinc-300 font-light leading-relaxed max-w-4xl border-l-2 border-[#ff7a63] pl-5 py-1 bg-white/[0.01]">
            {project.strategicImpact || project.shortDescription}
          </p>
        </div>
      </section>

      {/* 2. Key Results & Metrics Strip (World-Class Proof) */}
      {project.metrics && project.metrics.length > 0 && (
        <section className="py-10 border-b border-white/[0.08] bg-[#05131f]/50 backdrop-blur-md relative z-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#ff7a63] mb-6">
              <TrendingUp className="w-4 h-4" />
              <span>{language === 'TR' ? "Ölçülebilir Sonuçlar & Somut Kanıtlar" : "Key Measurable Results & Proof"}</span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {project.metrics.map((metric, idx) => (
                <div 
                  key={idx} 
                  className="bg-card/30 border border-white/[0.08] hover:border-[#ff7a63]/40 rounded-2xl p-5 transition-all duration-300 flex flex-col justify-center shadow-lg"
                >
                  <span className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-white tracking-tight mb-1 text-gradient bg-gradient-to-r from-white via-zinc-100 to-zinc-400 bg-clip-text text-transparent">
                    {metric.value}
                  </span>
                  <span className="text-xs font-semibold text-zinc-300 tracking-wide mb-0.5">
                    {metric.label}
                  </span>
                  {metric.detail && (
                    <span className="text-[11px] text-zinc-300 leading-snug">
                      {metric.detail}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 3. Cinema Media Spotlight */}
      <section className="py-12 md:py-16 bg-[#030811]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden border border-white/[0.12] shadow-2xl bg-black aspect-video">
            {project.videoUrl ? (
              <LazyVideoPlayer
                videoUrl={project.videoUrl}
                provider={project.videoProvider || null}
                coverImage={project.coverImage}
                title={project.title}
              />
            ) : (
              <img
                src={project.coverImage}
                alt={project.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
            )}
          </div>
        </div>
      </section>

      {/* 4. Structured 8-Part Case Study Narrative */}
      <section className="py-12 md:py-20 border-t border-white/[0.08] bg-[#040e18]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Main Case Study Flow (8 Columns) */}
          <div className="lg:col-span-8 flex flex-col gap-12">
            
            {/* 01. Brief / Sorun (The Challenge) */}
            {project.challenge && (
              <div className="bg-card/20 border border-white/[0.08] rounded-3xl p-6 sm:p-8 relative overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-[#ff7a63]/10 border border-[#ff7a63]/25 flex items-center justify-center text-[#ff7a63]">
                    <Target className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[#ff7a63]">01 · BRIEF & PROBLEM</span>
                    <h2 className="text-xl sm:text-2xl font-display text-white font-medium">
                      {language === 'TR' ? "Brief ve Çözülmesi Gereken Sorun" : "The Brief & The Core Challenge"}
                    </h2>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed">
                  {project.challenge}
                </p>
              </div>
            )}

            {/* 02. İçgörü ve Strateji (Insight & Strategy) */}
            {project.insight && (
              <div className="bg-card/20 border border-white/[0.08] rounded-3xl p-6 sm:p-8 relative overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-[#ff7a63]/10 border border-[#ff7a63]/25 flex items-center justify-center text-[#ff7a63]">
                    <Lightbulb className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[#ff7a63]">02 · STRATEGY & INSIGHT</span>
                    <h2 className="text-xl sm:text-2xl font-display text-white font-medium">
                      {language === 'TR' ? "İçgörü ve Stratejik Yaklaşım" : "Strategic Insight & Angle"}
                    </h2>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed">
                  {project.insight}
                </p>
              </div>
            )}

            {/* 03. Üretilen Yaratıcı Çözüm (The Creative Solution) */}
            {project.solution && (
              <div className="bg-card/20 border border-white/[0.08] rounded-3xl p-6 sm:p-8 relative overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-[#ff7a63]/10 border border-[#ff7a63]/25 flex items-center justify-center text-[#ff7a63]">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[#ff7a63]">03 · CREATIVE SOLUTION</span>
                    <h2 className="text-xl sm:text-2xl font-display text-white font-medium">
                      {language === 'TR' ? "Üretilen Yaratıcı Çözüm" : "The Creative & Visual Solution"}
                    </h2>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed">
                  {project.solution}
                </p>
              </div>
            )}

            {/* 04. Prodüksiyon veya Teknoloji Yaklaşımı (Production Craft) */}
            {project.productionCraft && (
              <div className="bg-card/20 border border-white/[0.08] rounded-3xl p-6 sm:p-8 relative overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-[#ff7a63]/10 border border-[#ff7a63]/25 flex items-center justify-center text-[#ff7a63]">
                    <Cpu className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[#ff7a63]">04 · PRODUCTION CRAFT</span>
                    <h2 className="text-xl sm:text-2xl font-display text-white font-medium">
                      {language === 'TR' ? "Prodüksiyon ve Teknoloji Zanaatı" : "Production Craft & Technology"}
                    </h2>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed">
                  {project.productionCraft}
                </p>
              </div>
            )}

            {/* 06. Teslimatlar ve Platformlar (Deliverables & Platforms) */}
            {project.deliverables && project.deliverables.length > 0 && (
              <div className="bg-card/20 border border-white/[0.08] rounded-3xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-xl bg-[#ff7a63]/10 border border-[#ff7a63]/25 flex items-center justify-center text-[#ff7a63]">
                    <Layers className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[#ff7a63]">05 · DELIVERABLES</span>
                    <h2 className="text-xl sm:text-2xl font-display text-white font-medium">
                      {language === 'TR' ? "Teslimatlar ve Dağıtım Formatları" : "Deliverables & Master Assets"}
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {project.deliverables.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-xs sm:text-sm text-zinc-200">
                      <CheckCircle2 className="w-4 h-4 text-[#ff7a63] shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}


            {/* Additional Project Gallery Grids */}
            {project.gallery && project.gallery.length > 0 && (
              <div className="flex flex-col gap-4">
                <h3 className="text-lg font-display text-white font-medium">
                  {language === 'TR' ? "Prodüksiyon ve Sahne Arkası Galerisi" : "Production & Gallery Stills"}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {project.gallery.map((img, idx) => (
                    <div key={idx} className="relative aspect-[16/10] bg-card rounded-2xl overflow-hidden border border-white/[0.08]">
                      <img 
                        src={img} 
                        alt={`${project.title} - Kare ${idx + 1}`} 
                        className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                        loading="lazy" 
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Sidebar Column: Project Specs, Team & Credits, CTA (4 Columns) */}
          <div className="lg:col-span-4 flex flex-col gap-8">
            
            {/* Quick Specs Overview */}
            <div className="bg-card/25 border border-white/[0.08] p-6 sm:p-7 rounded-3xl flex flex-col gap-5">
              <h3 className="text-xs font-bold uppercase tracking-widest text-white border-b border-white/[0.08] pb-3">
                {t.projectDetails}
              </h3>
              
              <div className="flex flex-col gap-4 text-xs sm:text-sm">
                <div className="flex items-center justify-between py-1 border-b border-white/[0.04]">
                  <span className="text-zinc-400 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#ff7a63]" />
                    {t.client}
                  </span>
                  <span className="font-medium text-white">{project.client}</span>
                </div>

                {project.year && (
                  <div className="flex items-center justify-between py-1 border-b border-white/[0.04]">
                    <span className="text-zinc-400 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#ff7a63]" />
                      {t.year}
                    </span>
                    <span className="font-medium text-white">{project.year}</span>
                  </div>
                )}

                <div className="flex items-center justify-between py-1 border-b border-white/[0.04]">
                  <span className="text-zinc-400 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#ff7a63]" />
                    {t.category}
                  </span>
                  <span className="font-medium text-white">{project.categories[0]}</span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className="text-zinc-400 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#ff7a63]" />
                    {language === 'TR' ? "Prodüksiyon Standartı" : "Production Standard"}
                  </span>
                  <span className="font-medium text-white">{project.productionStandard || "Cinema 4K RAW"}</span>
                </div>
              </div>
            </div>

            {/* 08. Tam Ekip ve Krediler (Full Team & Credits) */}
            {project.credits && project.credits.length > 0 && (
              <div className="bg-card/25 border border-white/[0.08] p-6 sm:p-7 rounded-3xl flex flex-col gap-5">
                <div className="flex items-center justify-between border-b border-white/[0.08] pb-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#ff7a63]" />
                    {language === 'TR' ? "Proje Künyesi" : "Project Credits"}
                  </h3>
                  <span className="text-[10px] font-mono text-zinc-300">
                    {project.credits.length} {language === 'TR' ? "Kişi/Rol" : "Roles"}
                  </span>
                </div>

                <div className="flex flex-col gap-3.5 text-xs">
                  {project.credits.map((c, idx) => {
                    const roleMap: Record<string, string> = {
                      "Müşteri": "Client",
                      "Ajans": "Agency",
                      "Prodüksiyon": "Production",
                      "Post Prodüksiyon": "Post-Production",
                      "Yönetmen": "Director",
                      "Yardımcı Yönetmen": "Assistant Director",
                      "Yapımcı": "Producer",
                      "Yapım Koordinatörü": "Production Coordinator",
                      "Yürütücü Yapımcı": "Executive Producer",
                      "Senarist": "Screenwriter",
                      "Kurgu": "Editor",
                      "Kurgucu": "Editor",
                      "Kurgu Yönetmeni": "Lead Editor",
                      "Kamera": "Camera",
                      "Kameraman": "Cameraman",
                      "Kamera Asistanı": "Camera Assistant",
                      "Görüntü Yönetmeni": "Director of Photography",
                      "Ses Teknisyeni": "Sound Technician",
                      "Işık Şefi": "Gaffer",
                      "Drone": "Drone",
                      "Drone Pilotu": "Drone Pilot",
                      "Sanat Yönetmeni": "Art Director",
                      "Renk Düzenleme": "Colorist",
                      "Renk": "Colorist",
                      "Özgün Müzik": "Original Score",
                      "Müzik": "Music & Sound Design",
                      "Sosyal Medya": "Social Media",
                      "Reji": "Assistant Direction & Stage",
                      "Canlı Yayın": "Live Broadcast",
                      "Etkinlik & Canlı Yayın Yönetmeni": "Event & Live Broadcast Director",
                      "Etkinlik & Yayın Yönetmeni": "Event & Live Broadcast Director",
                      "Sanat Yönetmeni & LED Ekran Tasarımı": "Art Director & LED Screen Design",
                      "Sanat Yönetmeni & LED Tasarımı": "Art Director & LED Screen Design",
                      "Sahne, Işık, Ses & LED Ekran Yönetimi": "Stage, Light, Sound & LED Directing",
                      "Sahne, Işık, Ses & LED Yönetimi": "Stage, Light, Sound & LED Directing",
                      "Kurumsal Kimlik, Logo ve Grafik Tasarım": "Brand Identity, Logo & Graphic Design",
                      "Kurumsal Kimlik & Logo Tasarımı": "Brand Identity & Logo Design",
                      "Gimbal Operatörü": "Gimbal Operator",
                      "Fotoğrafçı": "Still Photographer"
                    };
                    const displayRole = language === 'EN' ? (roleMap[c.role] || c.role) : c.role;
                    return (
                      <div key={idx} className="flex flex-col gap-0.5 border-b border-white/[0.04] pb-2.5 last:border-b-0 last:pb-0">
                        <span className="text-[10px] uppercase font-bold tracking-wider text-[#ff7a63]">{displayRole}</span>
                        <span className="font-medium text-zinc-200 text-sm">{c.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contact / Similar Project CTA Card */}
            <div className="bg-gradient-to-br from-[#d5503b]/20 via-card/30 to-card/10 border border-[#ff7a63]/30 p-6 sm:p-7 rounded-3xl flex flex-col gap-4 text-center">
              <h4 className="text-base font-display text-white font-semibold">
                {language === 'TR' ? "Benzer Standartta Bir Projeniz mi Var?" : "Planning a Similar Production?"}
              </h4>
              <p className="text-xs text-zinc-300 leading-relaxed">
                {language === 'TR' 
                  ? "Markanız için sinematik prodüksiyon, canlı yayın rejisi veya yaratıcı strateji geliştirelim."
                  : "Let's produce cinematic films, live broadcast streams, or creative digital campaigns for your brand."}
              </p>
              <Link
                to={language === 'EN' ? "/en/contact" : "/iletisim"}
                className="mt-2 w-full py-3 bg-[#ff7a63] hover:bg-[#ff7a63]/90 text-white text-xs font-bold uppercase tracking-wider rounded-full transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <span>{language === 'TR' ? "Teklif ve Fikir Alın" : "Request Proposal"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

          </div>

        </div>
      </section>

      {/* 5. Related Case Studies Showcase */}
      {relatedProjects.length > 0 && (
        <section className="py-20 md:py-24 bg-[#030811] border-t border-white/[0.08]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#ff7a63] block mb-1">
                  {language === 'TR' ? "DAHA FAZLA VAKA" : "EXPLORE MORE"}
                </span>
                <h2 className="text-2xl sm:text-4xl font-display text-white">
                  {t.relatedProjects}
                </h2>
              </div>
              <Link
                to={backProjectsUrl}
                className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white font-medium transition-colors"
              >
                <span>{language === 'TR' ? "Tüm Projeler" : "All Projects"}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {relatedProjects.map(p => {
                const pUrl = language === 'EN' ? `/en/projects/${p.slug}` : `/projeler/${p.slug}`;
                return (
                  <div key={p.id} className="flex flex-col gap-4 group bg-card/20 border border-white/[0.06] hover:border-[#ff7a63]/40 rounded-3xl p-4 transition-all duration-300 hover:-translate-y-1">
                    <Link to={pUrl} className="relative overflow-hidden aspect-video bg-zinc-900 rounded-2xl border border-white/10">
                      <img 
                        src={p.coverImage} 
                        alt={p.title} 
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                        loading="lazy" 
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Play className="w-10 h-10 text-white opacity-90" />
                      </div>
                    </Link>
                    <div className="flex flex-col flex-grow justify-between">
                      <div>
                        <span className="text-[10px] uppercase font-bold tracking-widest text-[#ff7a63] mb-1.5 block">
                          {p.categories[0]} {p.year ? `· ${p.year}` : ''}
                        </span>
                        <Link to={pUrl}>
                          <h3 className="text-base sm:text-lg font-display text-white group-hover:text-[#ff7a63] transition-colors leading-snug font-medium mb-2 line-clamp-2">
                            {p.title}
                          </h3>
                        </Link>
                        <p className="text-xs text-zinc-300 leading-relaxed line-clamp-2 mb-3">
                          {p.strategicImpact || p.shortDescription}
                        </p>
                      </div>
                      <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-zinc-400">
                        <span className="truncate max-w-[150px] font-medium text-zinc-400">{p.client}</span>
                        <Link to={pUrl} className="text-xs font-semibold text-zinc-300 group-hover:text-[#ff7a63] inline-flex items-center gap-1 transition-colors">
                          <span>{language === 'TR' ? "İncele" : "View"}</span>
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default ProjectDetail;
